import React from "react";
import { Layout } from "@/components/Layout";
import { NikLogo } from "@/components/Logo";
import { Link } from "react-router-dom";
import { Film, Globe, Heart, Sparkles, Zap, Users, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1762541693135-fb989de961e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CITY = "https://images.unsplash.com/photo-1666843527155-14ec5f016802?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const VALUES = [
  { icon: Film, title: "Story First", text: "Every KalamPrime Original starts with a story worth telling, not an algorithm." },
  { icon: Globe, title: "Rooted & Global", text: "Made in India in Marathi, Hindi, Tamil, Telugu and English — built for the world." },
  { icon: Heart, title: "For Every Family", text: "From kids' mornings to late-night premieres, one membership for the whole home." },
  { icon: Zap, title: "Press Play", text: "Fast, cinematic and effortless — your entertainment starts the moment you tap." },
];

const STATS = [
  { n: "4", label: "Plans for every viewer" },
  { n: "5", label: "Languages" },
  { n: "4K", label: "Ultra HD Premium" },
  { n: "24/7", label: "Live & On-Demand" },
];

export default function About() {
  return (
    <Layout>
      <div className="pt-28 px-4 md:px-12 pb-16 max-w-5xl mx-auto" data-testid="about-page">
        {/* Hero */}
        <div className="relative rounded-3xl overflow-hidden border border-white/10 h-72 sm:h-96 fade-up">
          <img src={HERO} alt="Cinema" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/60 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-6 md:p-10">
            <div className="mb-2"><NikLogo size={38} /></div>
            <p className="text-slate-300 max-w-xl text-sm sm:text-base">
              Your Entertainment. Your World. A KalamPrime story by Ishukart.
            </p>
          </div>
        </div>

        {/* Story */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-14 items-center">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">The Ishukart story</h1>
            <p className="text-slate-300 leading-relaxed mt-4">
              KalamPrime began with a simple belief at <span className="text-white font-semibold">Ishukart</span>: great stories shouldn't be locked behind borders, budgets or big-city cinemas.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              We built KalamPrime to be a home for Indian and international entertainment — blockbuster movies, binge-worthy web series, short films, kids' favourites, live events and creator-led content. Under the <span className="text-[var(--nik-gold)] font-semibold">KalamPrime Originals</span> label, we back bold storytellers from across the country and bring their work to screens everywhere.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              Your story starts here. Press play, and enter your world.
            </p>
          </div>
          <img src={CITY} alt="India" className="rounded-3xl border border-white/10 object-cover h-80 w-full" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#121824] border border-white/10 rounded-2xl p-6 text-center">
              <div className="font-display text-4xl font-extrabold text-[var(--nik-glow)]">{s.n}</div>
              <div className="text-xs text-slate-400 mt-2 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Values */}
        <h2 className="font-display text-3xl font-extrabold text-white mt-16 flex items-center gap-2">
          <Sparkles size={24} className="text-[var(--nik-gold)]" /> What we stand for
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {VALUES.map((v) => (
            <div key={v.title} className="bg-[#121824] border border-white/10 rounded-2xl p-6 hover:border-[var(--nik-blue)] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center">
                <v.icon size={20} className="text-white" />
              </div>
              <div className="font-display font-bold text-white mt-4 text-lg">{v.title}</div>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-3xl border border-[var(--nik-blue)]/40 bg-gradient-to-r from-[#12204a] to-[#121824] p-8 md:p-10 text-center">
          <Users size={28} className="mx-auto text-[var(--nik-glow)]" />
          <h3 className="font-display text-2xl font-bold text-white mt-3">Join the KalamPrime family</h3>
          <p className="text-slate-400 mt-2 max-w-md mx-auto">Pick a plan that fits you and start streaming today. Only on KalamPrime.</p>
          <div className="flex justify-center gap-3 mt-6 flex-wrap">
            <Link to="/plans" className="inline-flex items-center gap-2 bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold px-6 py-3 rounded-lg nik-glow-btn transition-colors">
              View Plans <ArrowRight size={17} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
